// ============================================
// hooks/useNotifications.js
// ============================================
import { useState, useEffect } from 'react';
import { 
  requestNotificationPermission, 
  subscribeToMessages 
} from '../models/notificationModel';
import { updateUserDeviceToken } from '../models/userModel';
import { trackEvent, AnalyticsEvents } from '../services/analyticsService';

export const useNotifications = (userId) => {
  const isE2E = typeof window !== 'undefined' && !!window.Cypress;
  const [notification, setNotification] = useState({ 
    open: false, 
    message: '', 
    title: '' 
  });
  const [permission, setPermission] = useState(() => (
    isE2E
      ? 'default'
      : (typeof Notification !== 'undefined' ? Notification.permission : 'default')
  ));

  useEffect(() => {
    if (isE2E) {
      // Skip Firebase Messaging subscription during Cypress E2E runs
      return () => {};
    }

    const unsubscribe = subscribeToMessages((payload) => {
      setNotification({
        open: true,
        message: payload.notification.body,
        title: payload.notification.title
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const requestPermission = async () => {
    try {
      // Cypress E2E path: rely on stubbed Notification.requestPermission and skip messaging
      if (isE2E) {
        const result = await (typeof Notification !== 'undefined' && typeof Notification.requestPermission === 'function'
          ? Notification.requestPermission()
          : Promise.resolve('granted'));

        if (result === 'granted') {
          setPermission('granted');
          setNotification({ open: true, message: 'Notifications enabled successfully', title: 'Success' });
          trackEvent(AnalyticsEvents.NOTIFICATION_OPT_IN, { granted: true });
          return { success: true };
        }

        setPermission('denied');
        setNotification({ open: true, message: 'Notification permission denied', title: 'Error' });
        trackEvent(AnalyticsEvents.NOTIFICATION_OPT_IN, { granted: false });
        return { success: false, error: 'Notification permission denied' };
      }

      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        throw new Error('VAPID key is not defined');
      }

      const token = await requestNotificationPermission(vapidKey);
      if (token && userId) {
        await updateUserDeviceToken(userId, token);
        setPermission('granted');
        setNotification({ open: true, message: 'Notifications enabled successfully', title: 'Success' });
        trackEvent(AnalyticsEvents.NOTIFICATION_OPT_IN, { granted: true });
        return { success: true };
      }
      return { success: false, error: 'Failed to get permission' };
    } catch (error) {
      console.error('Error getting notification permission:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    notification,
    setNotification,
    permission,
    requestPermission
  };
};